import { Box, type SxProps, type Theme } from '@mui/material'
import { useTranslate } from 'ra-core'
import { type ReactNode, useCallback } from 'react'
import { AutocompleteInput, useRecordContext, useResourceContext, useSourceContext, type Validator } from 'react-admin'
import { useWatch } from 'react-hook-form'

type ColorChoice = {
  name: string
  color: string
  label?: string
}

const DEFAULT_COLOR_CHOICES: ColorChoice[] = [
  { name: 'white', color: '#ffffff' },
  { name: 'black', color: '#000000' },
  { name: 'gray', color: '#808080' },
  { name: 'silver', color: '#c0c0c0' },
  { name: 'blue', color: '#0000ff' },
  { name: 'red', color: '#ff0000' },
  { name: 'green', color: '#008000' },
  { name: 'yellow', color: '#ffff00' },
  { name: 'orange', color: '#ffa500' },
  { name: 'brown', color: '#a52a2a' },
  { name: 'beige', color: '#f5f5dc' },
]

type AdvancedColorAutocompleteInputProps = {
  choices?: ColorChoice[]
  className?: string
  defaultValue?: string
  disabled?: boolean
  helperText?: ReactNode
  label?: string | false
  readOnly?: boolean
  source?: string
  sx?: SxProps<Theme>
  validate?: Validator | Validator[]
}

const ColorDot = ({ color }: { color: string }) => (
  <Box
    sx={{
      backgroundColor: color,
      border: '1px solid',
      borderColor: color === '#ffffff' ? 'divider' : 'transparent',
      borderRadius: '50%',
      height: 14,
      width: 14,
    }}
  />
)

const ColorOption = () => {
  const record = useRecordContext<ColorChoice>()

  if (!record) return null

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      <ColorDot color={record.color} />
      <span>{record.label ?? record.name}</span>
    </Box>
  )
}

const colorOptionText = <ColorOption />

const AdvancedColorAutocompleteInput = ({
  choices = DEFAULT_COLOR_CHOICES,
  source = 'color',
  ...props
}: AdvancedColorAutocompleteInputProps) => {
  const translate = useTranslate()
  const resource = useResourceContext()
  const sourceContext = useSourceContext()
  const finalSource = sourceContext.getSource(source)
  const selectedColorName = useWatch({ name: finalSource })
  const selectedColor = choices.find(({ name }) => name === selectedColorName)
  const getColorLabel = useCallback(
    ({ name }: ColorChoice) => translate(`resources.${resource}.enums.${source}.values.${name}`),
    [resource, source, translate],
  )
  const inputText = useCallback((choice: ColorChoice) => getColorLabel(choice), [getColorLabel])

  const matchSuggestion = useCallback(
    (filterValue: string, choice: ColorChoice) =>
      getColorLabel(choice).toLowerCase().includes(filterValue.toLowerCase()),
    [getColorLabel],
  )

  const getTranslatedChoices = choices.map((choice) => ({
    ...choice,
    label: getColorLabel(choice),
  }))

  return (
    <AutocompleteInput
      choices={getTranslatedChoices}
      noOptionsText="mymove.noOption"
      TextFieldProps={{
        InputProps: {
          startAdornment: selectedColor ? (
            <Box
              sx={{
                display: 'flex',
                marginLeft: 1,
              }}
            >
              <ColorDot color={selectedColor.color} />
            </Box>
          ) : null,
        },
      }}
      inputText={inputText}
      matchSuggestion={matchSuggestion}
      optionText={colorOptionText}
      optionValue="name"
      source={source}
      {...props}
    />
  )
}

export default AdvancedColorAutocompleteInput
